import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDate } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState, Pager } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Plans() {
  const qc = useQueryClient();
  const [editPlan, setEditPlan] = useState(null);
  const [form, setForm] = useState({});
  const [page, setPage] = useState(1);

  const { data: plans } = useQuery({ queryKey: ["admin-plans"], queryFn: async () => (await api.get("/admin/plans")).data });
  const { data: purchases } = useQuery({
    queryKey: ["plan-purchases", page],
    queryFn: async () => (await api.get(`/admin/plan-purchases?page=${page}`)).data,
  });

  const save = useMutation({
    mutationFn: async () => api.put(`/admin/plans/${editPlan.id}`, { name: form.name, price: Number(form.price), max_products: Number(form.max_products), annual_service_fee: Number(form.annual_service_fee), status: form.status }),
    onSuccess: () => { toast.success("Plan updated"); setEditPlan(null); qc.invalidateQueries({ queryKey: ["admin-plans"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delPlan = useMutation({
    mutationFn: async (id) => api.delete(`/admin/plans/${id}`),
    onSuccess: () => { toast.success("Plan deleted"); qc.invalidateQueries({ queryKey: ["admin-plans"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid="admin-plans-page">
      <PageHeader title="Plans & Payments" sub="One-time plans, annual service fee, purchase history." testid="plans-title" />

      <div className="grid gap-4 sm:grid-cols-3" data-testid="plans-grid">
        {(plans || []).map((p) => (
          <div key={p.id} data-testid={`plan-card-${p.slug}`} className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-[#1D5BD8]">{p.name}</p>
              <StatusBadge status={p.status} />
            </div>
            <p className="mt-2 text-3xl font-extrabold text-[#0F2440]" style={{ fontFamily: "Outfit, sans-serif" }}>{fmtINR(p.price)}</p>
            <p className="text-[11px] text-slate-500">one-time · {p.max_products} products · ₹{p.annual_service_fee?.toLocaleString("en-IN")}/yr from year 2</p>
            <div className="mt-3 flex justify-between border-t border-slate-100 pt-3 text-xs">
              <span className="text-slate-500">{p.agents} agents</span>
              <span className="font-semibold text-[#138808]">{fmtINR(p.revenue)} revenue</span>
            </div>
            <div className="mt-3 flex gap-2">
              <Button data-testid={`edit-plan-${p.slug}`} size="sm" variant="outline"
                onClick={() => { setEditPlan(p); setForm({ name: p.name, price: p.price, max_products: p.max_products, annual_service_fee: p.annual_service_fee, status: p.status }); }}
                className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50">Edit plan</Button>
              <Button data-testid={`delete-plan-${p.slug}`} size="sm" variant="outline"
                onClick={() => { if (window.confirm(`Delete plan "${p.name}"? Agents currently on it must be moved first.`)) delPlan.mutate(p.id); }}
                className="border-rose-200 text-rose-500 hover:bg-rose-50">Delete</Button>
            </div>
          </div>
        ))}
      </div>

      <h3 className="mb-3 mt-8 text-sm font-bold text-[#0F2440]">Plan Purchases</h3>
      {!purchases?.items?.length ? <EmptyState title="No purchases yet" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white shadow-sm">
          <table className="w-full text-left text-xs" data-testid="purchases-table">
            <thead>
              <tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {purchases.items.map((p) => (
                <tr key={p.id} data-testid={`purchase-${p.id}`} className="border-b border-slate-50">
                  <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">{fmtDate(p.created_at)}</td>
                  <td className="px-4 py-2.5 font-medium text-[#0F2440]">{p.agent_name}</td>
                  <td className="px-4 py-2.5 capitalize text-slate-500">{p.type}</td>
                  <td className="px-4 py-2.5 text-slate-600">{p.description}</td>
                  <td className="px-4 py-2.5 font-semibold text-[#0F2440]">{fmtINR(p.amount)}</td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={p.payment_status} />
                    {p.test && <span className="ml-1.5 rounded bg-amber-100 px-1 text-[9px] font-bold text-amber-700">TEST</span>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[10px] text-slate-500">{p.invoice_no || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {purchases && <Pager page={purchases.page} pages={purchases.pages} onPage={setPage} />}

      <Dialog open={!!editPlan} onOpenChange={() => setEditPlan(null)}>
        <DialogContent className="border-slate-200 bg-white text-[#0F2440]">
          <DialogHeader><DialogTitle>Edit {editPlan?.name} plan</DialogTitle></DialogHeader>
          {editPlan && (
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-500">Name</Label>
                <Input data-testid="plan-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 border-slate-200" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-slate-500">Price ₹</Label>
                  <Input data-testid="plan-price-input" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 border-slate-200" /></div>
                <div><Label className="text-xs text-slate-500">Product limit</Label>
                  <Input data-testid="plan-limit-input" type="number" value={form.max_products} onChange={(e) => setForm({ ...form, max_products: e.target.value })} className="mt-1 border-slate-200" /></div>
                <div><Label className="text-xs text-slate-500">Service fee ₹/yr</Label>
                  <Input data-testid="plan-fee-input" type="number" value={form.annual_service_fee} onChange={(e) => setForm({ ...form, annual_service_fee: e.target.value })} className="mt-1 border-slate-200" /></div>
              </div>
              <div className="flex gap-2">
                {["active", "inactive"].map((st) => (
                  <button key={st} data-testid={`plan-status-${st}`} onClick={() => setForm({ ...form, status: st })}
                    className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium capitalize ${form.status === st ? "border-[#FF6B00] bg-orange-50 text-[#E85D04]" : "border-slate-200 text-slate-500"}`}>
                    {st}
                  </button>
                ))}
              </div>
              <Button data-testid="plan-save-btn" onClick={() => save.mutate()} disabled={save.isPending}
                className="w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">Save plan</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
